// frontend/src/services/ttsApi.js

// 使用相对路径，通过Nginx代理访问后端API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const API_PREFIX = '/api/v1/tts';

const getAccessToken = () => localStorage.getItem('access_token') || localStorage.getItem('token') || '';

const getUserIdHeaderValue = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return '';
  try {
    const user = JSON.parse(userStr);
    return user?.id ? String(user.id) : '';
  } catch (_) {
    return '';
  }
};

const TTSApi = {
  /**
   * 将文本合成为语音，返回可播放的音频 URL
   * @param {string} text - 需要合成的文本
   * @param {string} [voice='Serena'] - 音色，可选: Chelsie、Cherry、Ethan、Serena 等
   * @returns {Promise<string>} 返回音频文件 URL
   */
  synthesize: async (text, voice = 'Serena') => {
    if (!text) throw new Error('Text is required for TTS synthesis');
    const url = `${API_BASE_URL}${API_PREFIX}/synthesize`;
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
      },
      body: JSON.stringify({ text, voice }),
    });

    if (!resp.ok) {
      throw new Error(`TTS 请求失败: ${resp.status}`);
    }

    const data = await resp.json();
    if (!data.success || !data.url) {
      throw new Error('TTS 合成失败');
    }
    return data.url;
  },

  /**
   * 以流式方式合成语音，返回 PCM Base64 块
   * @param {string} text 文本
   * @param {function(string):void} onData 收到单个Base64 PCM块回调
   * @param {function():void} onDone 完成回调
   * @param {function(Error):void} onError 错误回调
   * @param {string} [voice='longyumi_v2'] 音色
   * @param {AbortSignal} [signal] 取消信号
   * @param {string} [interviewer_type] 面试官类型
   * @param {string} [language] 面试语言
   */
  stream: async (
    text,
    onData,
    onDone,
    onError,
    voice = 'longyumi_v2',
    signal = undefined,
    interviewer_type = undefined,
    language = undefined,
  ) => {
    if (!text) return;
    const url = `${API_BASE_URL}${API_PREFIX}/stream`;

    const processLine = (line) => {
      if (!line.startsWith('data:')) return false;
      const payload = line.slice(5).trim();
      if (payload === '[DONE]') return true;
      onData && onData(payload);
      return false;
    };

    try {
      const token = getAccessToken();
      const userId = getUserIdHeaderValue();
      const resp = await fetch(url, {
        signal,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(userId ? { 'X-User-Id': userId } : {}),
        },
        body: JSON.stringify({ text, voice, interviewer_type, language }),
      });

      if (!resp.ok || !resp.body) throw new Error(`TTS stream 请求失败: ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 1);
          if (processLine(line.trim())) {
            onDone && onDone();
            return;
          }
        }
      }
      onDone && onDone();
    } catch (e) {
      onError && onError(e);
    }
  },
};

export default TTSApi;
